import storage from '../../../../../hooks/useStorage/useStorage';
import { AvailabilityEnum } from '../../../../products/ProductTypes';
import { isPreOrder } from '../../../../products/utils/availability.utils';
import { OfflineOrder } from '../../../domain/offlineOrder/OfflineOrder';
import { offlineCategoriesRepository } from '../../../repositories/offlineCategories.repository';
import { offlineClassificationsRepository } from '../../../repositories/offlineClassifications.repository';
import { offlineCollectionsRepository } from '../../../repositories/offlineCollections.repository';
import { offlineCustomersRepository } from '../../../repositories/offlineCustomers.repository';
import { offlineDeliveryWindowsRepository } from '../../../repositories/offlineDeliveryWindows.repository';
import { offlineFutureStocksRepository } from '../../../repositories/offlineFutureStocks.repository';
import { offlineKitsRepository } from '../../../repositories/offlineKits.repository';
import { offlinePacksRepository } from '../../../repositories/offlinePacks.repository';
import { offlinePriceTablesRepository } from '../../../repositories/offlinePriceTables.repository';
import { offlineProductColorPriceInfoRepository } from '../../../repositories/offlineProductColorPriceInfo.repository';
import { offlineProductColorsRepository } from '../../../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../../../repositories/offlineProducts.repository';
import { offlineSalesRepresentativeRepository } from '../../../repositories/offlineSalesRepresentative.repository';
import { offlineSellersRepository } from '../../../repositories/offlineSellers.repository';
import { offlineSizesRepository } from '../../../repositories/offlineSizes.repository';
import { offlineSkusRepository } from '../../../repositories/offlineSkus.repository';
import { offlineStocksRepository } from '../../../repositories/offlineStocks.repository';
import { offlineUsersRepository } from '../../../repositories/offlineUsers.repository';
import {
  ChangeProductGridBasedOnPacksParams,
  CreateGridBasedOnPacksParams,
  CreateGridParams,
} from '../types/offlineOrders.actions.types';
import {
  GetArtifactsToAddProductBasedOnGridParams,
  GridItemWithArtifacts,
} from './types/offlineOrderArtifactsGenerator.utils.types';

const getArtifactsToAddProductBasedOnGrid = async ({
  order,
  gridItems,
  kitId,
}: GetArtifactsToAddProductBasedOnGridParams) => {
  if (!gridItems[0]) {
    throw new Error('grid items array is empty');
  }

  const { deliveryWindowId, skuId, availability } = gridItems[0];
  const isPreOrderOrderProduct = isPreOrder(availability);

  const [deliveryWindow, firstSkuPossibleUndefined] = await Promise.all([
    isPreOrderOrderProduct && deliveryWindowId
      ? offlineDeliveryWindowsRepository.getById(deliveryWindowId)
      : undefined,
    offlineSkusRepository.getById(skuId),
  ]);

  const productColor = firstSkuPossibleUndefined
    ? await offlineProductColorsRepository.getById(firstSkuPossibleUndefined.productColorId)
    : undefined;

  if (!productColor || (isPreOrderOrderProduct && !deliveryWindow)) {
    console.error('getArtifactsToAddProductBasedOnGrid', {
      productColor,
      deliveryWindow,
    });
    throw new Error('product color or delivery window');
  }

  const [productColorPriceInfo, product, allSkus] = await Promise.all([
    offlineProductColorPriceInfoRepository.getById(productColor.id, order.priceTable.id),
    offlineProductsRepository.getById(productColor.productId),
    offlineSkusRepository.findByProductColorId(productColor.id),
  ]);

  if (
    !productColorPriceInfo ||
    !product ||
    gridItems.some((gridItem) => !allSkus.find((sku) => sku.id === gridItem.skuId))
  ) {
    console.error('getArtifactsToAddProductBasedOnGrid', {
      product,
      allSkus,
      productColorPriceInfo,
    });
    throw new Error('product, sku or product color price info not found');
  }

  const [category, collection, classifications] = await Promise.all([
    product.categoryId ? offlineCategoriesRepository.getById(product.categoryId) : undefined,
    offlineCollectionsRepository.getById(product.collectionId),
    !productColor.classificationIds.length
      ? Promise.resolve([])
      : offlineClassificationsRepository.findByIds(productColor.classificationIds),
  ]);

  const futureStocks =
    availability === AvailabilityEnum.FUTURE_STOCK
      ? await offlineFutureStocksRepository.findByProductColorAndDeliveryWindowId({
          deliveryWindowId,
          productColorId: productColor.id,
        })
      : undefined;

  const stocks =
    availability === AvailabilityEnum.READY_FOR_DELIVERY
      ? await offlineStocksRepository.findByProductColorId({
          productColorId: productColor.id,
        })
      : undefined;

  const artifactsToGridItem: GridItemWithArtifacts[] = [];

  for (const sku of allSkus) {
    const stock = stocks?.find((stock) => stock.skuId === sku.id);
    const futureStock = futureStocks?.find((stock) => stock.skuId === sku.id);
    const currentGridItem = gridItems.find((gridItem) => gridItem.skuId === sku.id);

    const size = await offlineSizesRepository.getById(sku.sizeId);
    if (!size) {
      throw new Error('size not found');
    }

    artifactsToGridItem.push({
      sku,
      size,
      stock,
      futureStock,
      id: currentGridItem?.id,
      quantity: currentGridItem?.quantity || 0,
    });
  }

  const kit = kitId ? await offlineKitsRepository.getById(kitId) : undefined;
  if (kitId && !kit) {
    console.error('adding product based on grid: kit not found', kitId);
    throw new Error('adding product based on grid: kit not found with id ' + kitId);
  }

  return {
    kit,
    product,
    category,
    collection,
    productColor,
    deliveryWindow,
    classifications,
    artifactsToGridItem,
    productColorPriceInfo,
  };
};

export const offlineOrderArtifactsGenerator = {
  getArtifactsToAddProductBasedOnGrid,
  getArtifactsToAddProductBasedOnPacks: async (
    order: OfflineOrder,
    orderProductAndPacks: CreateGridBasedOnPacksParams,
  ) => {
    const offlineSku = await offlineSkusRepository.findOneByProductColorId(
      orderProductAndPacks.productColorId,
    );

    if (!offlineSku) {
      throw new Error('sku not found');
    }

    const skuId = offlineSku.id;

    const gridItems = [
      orderProductAndPacks.availability !== AvailabilityEnum.READY_FOR_DELIVERY
        ? {
            skuId,
            quantity: 0,
            availability: orderProductAndPacks.availability,
            productColorId: orderProductAndPacks.productColorId,
            deliveryWindowId: orderProductAndPacks.deliveryWindowId,
          }
        : {
            skuId,
            quantity: 0,
            availability: orderProductAndPacks.availability,
            productColorId: orderProductAndPacks.productColorId,
          },
    ];
    const commonArtifacts = await getArtifactsToAddProductBasedOnGrid({ order, gridItems });

    const packIdsToSearch = orderProductAndPacks.packIdsWithMultiplier.map(
      (packWithMultiplier) => packWithMultiplier.packId,
    );

    const packs = await offlinePacksRepository.findByIds(packIdsToSearch);
    const packsWithMultiplier = orderProductAndPacks.packIdsWithMultiplier.map(
      (packWithMultiplier) => {
        const pack = packs.find((pack) => pack.id === packWithMultiplier.packId);
        if (!pack) {
          throw new Error('pack not found');
        }
        return {
          pack,
          multiplier: packWithMultiplier.multiplier,
        };
      },
    );

    return { ...commonArtifacts, packsWithMultiplier };
  },
  getArtifactsToChangeProductGridBasedOnPacks: async ({
    orderProductIds,
    packIdsWithMultiplier,
  }: ChangeProductGridBasedOnPacksParams) => {
    const productColor = await offlineProductColorsRepository.getById(
      orderProductIds.productColorId,
    );

    if (!productColor) {
      throw new Error('product color not found');
    }

    const packIdsToSearch = packIdsWithMultiplier.map(
      (packWithMultiplier) => packWithMultiplier.packId,
    );

    const packs = await offlinePacksRepository.findByIds(packIdsToSearch);
    const packsWithMultiplier = packIdsWithMultiplier.map((packWithMultiplier) => {
      const pack = packs.find((pack) => pack.id === packWithMultiplier.packId);
      if (!pack) {
        throw new Error('pack not found');
      }
      return {
        pack,
        multiplier: packWithMultiplier.multiplier,
      };
    });

    return {
      packsWithMultiplier,
      productColorWithAllPacks: productColor,
    };
  },
  getArtifactsToCreateOrder: async (customerId: string | undefined) => {
    const teamId = storage.getTeamId();

    const userTeam = offlineUsersRepository.getUserTeam(teamId);
    if (!userTeam) {
      throw new Error('team not found');
    }

    const seller = await offlineSellersRepository.getById(userTeam.userId);
    const customer = customerId ? await offlineCustomersRepository.getById(customerId) : undefined;

    const salesRepresentative =
      await offlineOrderArtifactsGenerator.getSalesRepForOrder(customerId);

    const customerPriceTable = customer?.priceTableId
      ? await offlinePriceTablesRepository.getById(customer.priceTableId)
      : undefined;

    const priceTable = customerPriceTable || (await offlinePriceTablesRepository.getDefault());

    if (!seller || !priceTable) {
      throw new Error('seller or price table not found');
    }

    return { customer, salesRepresentative, seller, priceTable };
  },

  getSalesRepForOrder: async (customerId?: string) => {
    if (!customerId) {
      return offlineSalesRepresentativeRepository.getSalesRepFromCurrentUser();
    }

    const principalCustomerSalesRep =
      await offlineCustomersRepository.getCustomerPrincipalSalesRepresentative(customerId);

    return (
      principalCustomerSalesRep ?? offlineSalesRepresentativeRepository.getSalesRepFromCurrentUser()
    );
  },

  getArtifactsToAddAllProducts: async () => {
    const allSkus = await offlineSkusRepository.findAll();
    const allFutureStocks = await offlineFutureStocksRepository.findAll();

    const gridItemsToCreate = allSkus.flatMap((sku) => {
      const futureStockToProductColor = allFutureStocks.filter(
        (futureStock) => futureStock.productColorId === sku.productColorId,
      );

      const preOrderGridItems = sku.preOrderDeliveryWindowIds?.map<CreateGridParams>(
        (deliveryWindowId) => ({
          deliveryWindowId,
          quantity: 1,
          skuId: sku.id,
          productColorId: sku.productColorId,
          availability: AvailabilityEnum.INFINITE_PRE_ORDER,
        }),
      );

      const readyForDeliveryGridItems: CreateGridParams = {
        quantity: 1,
        skuId: sku.id,
        productColorId: sku.productColorId,
        availability: AvailabilityEnum.READY_FOR_DELIVERY,
      };

      const futureStockGridItems = futureStockToProductColor?.map<CreateGridParams>(
        (futureStock) => ({
          quantity: 1,
          skuId: sku.id,
          productColorId: sku.productColorId,
          availability: AvailabilityEnum.FUTURE_STOCK,
          deliveryWindowId: futureStock.deliveryWindowId,
        }),
      );

      return [
        readyForDeliveryGridItems,
        ...(preOrderGridItems || []),
        ...(futureStockGridItems || []),
      ];
    });

    return gridItemsToCreate;
  },
};
